import { hasWindow } from "@omni/utils/dom.utils";
import { attempt } from "@omni/utils/function.utils";
import * as LocalStorage from "@omni/utils/localStorage.utils";
import * as SessionStorage from "@omni/utils/sessionStorage.utils";
import { AxiosResponse } from "axios";
import { ThunkDispatch } from "redux-thunk";
import aa from "search-insights";

import { createApiFor } from "@/api/hooks/utils";
import { set } from "@/redux/modules/value/search/search.module";
import { AlgoliaParameters as SearchParameters } from "@/redux/modules/value/search/search.types";
import { IState, TExtraThunkArgs, TValueAction } from "@/redux/store.types";
import { GetSearchParametersRequestModel } from "@/types/service/search/getSearchParametersRequest.model";
import { GetSearchParametersResponseModel } from "@/types/service/search/getSearchParametersResponse.model";
import { Config } from "@/utils/config.utils";
import { StorageKeys } from "@/utils/enums";

enum SearchParametersRequestStatus {
    IDLE = "IDLE",
    PENDING = "PENDING"
}

const axios = createApiFor(`/${Config.general.basestore}`, { bff: true });

let status = SearchParametersRequestStatus.IDLE;

const searchParametersPending = () =>
    status === SearchParametersRequestStatus.PENDING;

const getSessionsStorageSearchParameters = () => {
    if (!hasWindow()) return undefined;

    attempt<SearchParameters | undefined>(
        () =>
            JSON.parse(
                SessionStorage.getItem(StorageKeys.SearchParameters) ?? ""
            ),
        undefined
    );
};

export const initAlgoliaParameters = async () => {
    return (
        getSessionsStorageSearchParameters() ??
        (await generateSearchParameters(window.storeManager.dispatch))
    );
};

export const getUserToken = () => {
    return LocalStorage.getItem(StorageKeys.AlgoliaUserToken)!;
};

export const getAuthenticatedUserToken = () => {
    return (
        LocalStorage.getItem(StorageKeys.AlgoliaAuthenticatedUserToken) ||
        undefined
    );
};

const setSessionsStorageSearchParameters = (
    algoliaParameters: SearchParameters
) =>
    SessionStorage.setItem(
        StorageKeys.SearchParameters,
        JSON.stringify(algoliaParameters)
    );

const generateSearchParameters: (
    dispatch: ThunkDispatch<IState, TExtraThunkArgs, TValueAction>
) => Promise<SearchParameters> = async (dispatch) => {
    status = SearchParametersRequestStatus.PENDING;

    const { data } = await axios.post<
        GetSearchParametersResponseModel,
        AxiosResponse<GetSearchParametersResponseModel>,
        GetSearchParametersRequestModel
    >(`/search/parameters`, {
        userToken: getAuthenticatedUserToken() ?? getUserToken()
    });

    dispatch(
        set({
            algoliaParameters: data
        })
    );

    status = SearchParametersRequestStatus.IDLE;

    setSessionsStorageSearchParameters(data);

    aa("init", {
        partial: true,
        appId: data.appId,
        apiKey: data.searchApiKey
    });

    return data;
};

export {
    generateSearchParameters,
    getSessionsStorageSearchParameters,
    searchParametersPending,
    setSessionsStorageSearchParameters
};
